// SupplierPickerXL - Sprint 44 Fase 3 Vista 9.
// Autocomplete de proveedores tokenizado. Reusa /api/proveedores/search.
// Sin libs externas. Reemplazo XL de modules/importacion-usa/shared/SupplierPicker.
import { useCallback, useEffect, useRef, useState } from 'react';
import { ensureKeyframes, injectCSS } from '../../design/motion';
import apiClient from '../../api/apiClient';

const CSS_ID = 'xl-sup-picker-css';
const CSS = `
.xl-sp { position: relative; font-family: var(--xl-font-sans) }
.xl-sp__field { display: flex; flex-direction: column; gap: 4px }
.xl-sp__label {
  font-size: 12px; font-weight: 500;
  color: var(--xl-color-text-secondary);
}
.xl-sp__input {
  width: 100%;
  font: 400 14px/1.4 var(--xl-font-sans);
  color: var(--xl-color-text);
  background: var(--xl-color-bg);
  border: 1px solid var(--xl-color-border);
  border-radius: var(--xl-radius-md);
  padding: 9px 12px;
  box-sizing: border-box;
}
.xl-sp__input:focus {
  outline: none;
  border-color: var(--xl-color-focus);
  box-shadow: 0 0 0 3px rgba(37,99,235,0.18);
}
[data-xl-theme="dark"] .xl-sp__input:focus {
  box-shadow: 0 0 0 3px rgba(96,165,250,0.28);
}
.xl-sp__current {
  display: inline-flex; align-items: center; gap: 8px;
  margin-top: 4px;
  font-size: 12px; color: var(--xl-color-success);
}
.xl-sp__clear {
  font-size: 11px; color: var(--xl-color-text-secondary);
  background: transparent; border: none; cursor: pointer;
  text-decoration: underline;
}

.xl-sp__menu {
  position: absolute; z-index: 30;
  top: 100%; left: 0; right: 0;
  margin-top: 4px;
  background: var(--xl-color-surface);
  border: 1px solid var(--xl-color-border);
  border-radius: var(--xl-radius-md);
  box-shadow: var(--xl-shadow-md);
  max-height: 300px; overflow-y: auto;
}
.xl-sp__row {
  display: block; width: 100%;
  text-align: left;
  background: transparent; border: none; cursor: pointer;
  padding: 10px 12px;
  color: var(--xl-color-text);
  font-family: inherit;
  border-bottom: 1px solid var(--xl-color-border);
}
.xl-sp__row:last-child { border-bottom: 0 }
.xl-sp__row:hover { background: var(--xl-color-surface-alt) }
.xl-sp__row-name { font-weight: 600; font-size: 13px }
.xl-sp__row-sub { font-size: 11px; color: var(--xl-color-text-muted); margin-top: 2px }
.xl-sp__hint { padding: 12px; font-size: 12px; color: var(--xl-color-text-muted) }
.xl-sp__err { font-size: 11px; color: var(--xl-color-danger); margin-top: 2px }
`;

export default function SupplierPickerXL({
  currentName, currentSupplierId,
  onSelectSupplier, onClear,
  required = false, error,
  label = 'Proveedor',
  placeholder = 'Buscar proveedor...',
}) {
  useEffect(() => { ensureKeyframes(); injectCSS(CSS_ID, CSS); }, []);

  const [query, setQuery] = useState(currentName || '');
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const ref = useRef(null);
  const debRef = useRef(null);

  useEffect(() => { setQuery(currentName || ''); }, [currentName]);

  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);

  const doSearch = useCallback(async (q) => {
    setLoading(true);
    try {
      const r = await apiClient.get('/proveedores/search?q=' + encodeURIComponent(q || ''));
      setResults((r && r.data && r.data.items) || []);
    } catch (_) { setResults([]); }
    finally { setLoading(false); }
  }, []);

  const handleInput = (v) => {
    setQuery(v);
    setOpen(true);
    if (debRef.current) clearTimeout(debRef.current);
    debRef.current = setTimeout(() => doSearch(v), 300);
  };

  const pick = (s) => {
    setQuery(s.nombre || '');
    setOpen(false);
    if (onSelectSupplier) onSelectSupplier(s);
  };

  const clear = () => {
    setQuery('');
    if (onClear) onClear();
  };

  return (
    <div ref={ref} className="xl-sp">
      <div className="xl-sp__field">
        <span className="xl-sp__label">
          {label}{required ? ' *' : ''}
        </span>
        <input type="text"
          className="xl-sp__input"
          value={query}
          onChange={(e) => handleInput(e.target.value)}
          onFocus={() => { if (!results.length && !loading) doSearch(''); setOpen(true); }}
          placeholder={placeholder}
          autoComplete="off"
          aria-invalid={error ? 'true' : undefined} />
        {currentSupplierId && currentName && (
          <span className="xl-sp__current">
            <span>{'\u2713'} Seleccionado: <b>{currentName}</b></span>
            <button type="button" className="xl-sp__clear" onClick={clear}>Cambiar</button>
          </span>
        )}
        {error && <span className="xl-sp__err">{error}</span>}
      </div>
      {open && (
        <div className="xl-sp__menu" role="listbox">
          {loading && <div className="xl-sp__hint">Buscando\u2026</div>}
          {!loading && results.length === 0 && <div className="xl-sp__hint">Sin resultados.</div>}
          {!loading && results.map((s) => (
            <button key={s.id} type="button" className="xl-sp__row"
              onClick={() => pick(s)}>
              <div className="xl-sp__row-name">{s.nombre}</div>
              <div className="xl-sp__row-sub">
                {s.email || ''} {s.pais ? '\u00b7 ' + s.pais : ''}
                {s.moneda_principal ? ' \u00b7 ' + s.moneda_principal : ''}
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
